import React, {useEffect, useState} from "react";
import {api, formatApiErrorDetail} from "@/lib/api";
import {toast} from "sonner";

export default function ContentReview({tests, onSaved}) {
  const [selected, setSelected] = useState("");
  const [questions, setQuestions] = useState(null);
  const [reviewer, setReviewer] = useState("");
  const [note, setNote] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const fail = e => toast.error(formatApiErrorDetail(e.response?.data?.detail));
  const loadPending = () => api.get("/admin/pending-reviews").then(r => setPending(r.data)).catch(fail);
  useEffect(() => { loadPending(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const inspect = async id => {
    setSelected(id); setQuestions(null); setConfirmed(false);
    if (!id) return;
    try { setQuestions((await api.get(`/admin/tests/${id}/questions`)).data); } catch(e) { fail(e); }
  };
  const publish = async e => {
    e.preventDefault(); if (!confirmed || busy) return; setBusy(true);
    try {
      await api.post(`/admin/tests/${selected}/publish`, {reviewer_name: reviewer, review_note: note});
      toast.success("Schválená verze testu je publikovaná.");
      setQuestions(null); setSelected(""); setConfirmed(false); await onSaved();
    } catch(e) { fail(e); } finally { setBusy(false); }
  };
  return <section className="mb-12 p-6 border border-stone bg-bone space-y-5">
    <h2 className="font-serif text-2xl">Kontrola obsahu před zveřejněním</h2>
    <p>Projděte všechny texty, zadání, klíče, obrázky a vysvětlení. Automatická kontrola nenahrazuje kontrolu učitele.</p>
    <select aria-label="Návrh ke kontrole" className="p-2 border w-full" value={selected} onChange={e => inspect(e.target.value)}>
      <option value="">Vyberte návrh testu</option>
      {tests.filter(t => t.status === "draft").map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
    </select>
    {questions && <>
      <div className="max-h-[600px] overflow-auto space-y-6">
        {questions.map((q,i) => <article key={q.id} className="border-t pt-4">
          <h3 className="font-bold">{i+1}. {q.question_text} ({q.points} b.)</h3>
          {q.source_text && <p className="whitespace-pre-wrap my-3">{q.source_text}</p>}
          {q.source_text_id && !q.source_text && <p>Výchozí text: {q.source_text_id}</p>}
          <p>{q.instruction}</p>
          {q.image_url && <img src={q.image_url} alt="Obrázek k úloze" />}
          {q.options?.map((o,j) => <p key={j}>{String.fromCharCode(65+j)}. {o}</p>)}
          {["subquestions", "matching_items", "matching_choices", "ordering_parts"].map(field => q[field]?.map(row => <p key={field+row.label}>{row.label}: {row.text}</p>))}
          <p className="mt-2">Klíč: {JSON.stringify(q.correct_answer)}</p>
          <p>Hodnocení: {q.scoring_rule}; {q.score_by_correct ? `body podle počtu správných: ${q.score_by_correct.join(", ")}` : ""}</p>
          {q.rubric && <p>Rubrika: {q.rubric}</p>}
          <p className="my-2">{q.explanation}</p>
        </article>)}
      </div>
      <form onSubmit={publish} className="space-y-3">
        <input className="p-2 border w-full" aria-label="Jméno kontrolujícího učitele" placeholder="Jméno kontrolujícího učitele" minLength={3} value={reviewer} onChange={e => setReviewer(e.target.value)} required />
        <textarea className="p-2 border w-full" aria-label="Záznam kontroly" placeholder="Co bylo zkontrolováno a s jakým výsledkem" minLength={10} value={note} onChange={e => setNote(e.target.value)} required />
        <label className="block"><input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} required /> Potvrzuji odbornou kontrolu všech úloh, klíčů a bodování.</label>
        <button disabled={!confirmed || busy} className="bg-navy text-white p-3 disabled:opacity-40">Schválit a publikovat</button>
      </form>
    </>}
    <h2 className="font-serif text-2xl pt-6">Odpovědi čekající na učitele ({pending.length})</h2>
    {pending.map(row => <ManualReview key={row.attempt_id+row.question.id} row={row} onSaved={loadPending} />)}
  </section>;
}

function ManualReview({row, onSaved}) {
  const [points, setPoints] = useState(0), [comment, setComment] = useState(""), [busy, setBusy] = useState(false);
  const submit = async e => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/admin/attempts/${row.attempt_id}/grade/${row.question.id}`, {points: Number(points), comment}); await onSaved(); }
    catch(e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return <form onSubmit={submit} className="border-t pt-4 space-y-3">
    <p>{row.question.question_text}</p><p>Odpověď žáka: {String(row.user_answer)}</p><p>Rubrika: {row.question.rubric}</p>
    <label>Body <input type="number" min="0" max={row.question.points} value={points} onChange={e => setPoints(e.target.value)} required /></label>
    <input className="p-2 border w-full" placeholder="Vysvětlení hodnocení pro žáka" aria-label="Komentář učitele" minLength={5} value={comment} onChange={e => setComment(e.target.value)} required />
    <button disabled={busy} className="bg-navy text-white p-2">Uložit hodnocení</button>
  </form>;
}
